import { db } from './firebase';
import { localAgentService } from './localAgentService';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  setDoc,
  getDoc,
  runTransaction
} from 'firebase/firestore';

/**
 * Merkezi Sipariş Yönetim Servisi
 * Tüm sipariş türleri için tek veritabanı koleksiyonu
 */
export class OrdersService {
  constructor(businessId) {
    this.businessId = businessId;
    this.ordersRef = collection(db, 'businesses', 'food_beverage', 'businesses', businessId, 'orders');
    this.countersRef = doc(db, 'businesses', 'food_beverage', 'businesses', businessId, 'system', 'counters');
  }

  /**
   * Helper function to clean undefined values from object
   * Firestore doesn't support undefined values
   */
  static cleanUndefinedValues(obj) {
    if (obj === null || obj === undefined) return obj;
    
    // Handle Arrays
    if (Array.isArray(obj)) {
      return obj
        .map(item => (typeof item === 'object' && item !== null) ? this.cleanUndefinedValues(item) : item)
        .filter(item => item !== undefined);
    }

    // Handle Objects
    if (typeof obj === 'object') {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip undefined values
        if (value === undefined) continue;

        // Recursively clean objects and arrays
        if (Array.isArray(value)) {
          cleaned[key] = value
            .map(item => (typeof item === 'object' && item !== null) ? this.cleanUndefinedValues(item) : item)
            .filter(item => item !== undefined);
        } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
          cleaned[key] = this.cleanUndefinedValues(value);
        } else {
          cleaned[key] = value;
        }
      }
      return cleaned;
    }

    return obj;
  }

  /**
   * Yeni sipariş oluştur
   * @param {Object} orderData - Sipariş verisi
   * @returns {Promise<string>} Oluşturulan sipariş ID'si
   */
  async createOrder(orderData) {
    try {
      const orderDoc = {
        // Temel bilgiler
        orderId: orderData.orderId || this.generateOrderId(orderData.orderSource),
        orderNumber: orderData.orderNumber || this.generateOrderNumber(),
        displayOrderNumber: orderData.displayOrderNumber || await this.generateDailyShortOrderNumber(),
        orderSource: orderData.orderSource, // 'table_service', 'phone_order', 'counter_sale', 'qr_menu', 'kiosk'

        // Durum yönetimi
        status: orderData.status || 'pending', // pending, preparing, ready, served, completed, cancelled
        paymentStatus: orderData.paymentStatus || 'pending', // pending, partial, paid, refunded

        // Kaynak spesifik bilgiler
        sourceDetails: {
          // Table Service
          tableId: orderData.tableId || orderData.sourceDetails?.tableId || null,
          tableName: orderData.sourceDetails?.tableName || null,
          tableSessionId: orderData.sourceDetails?.tableSessionId || null, // Mesa session ID
          sectionId: orderData.sectionId || orderData.sourceDetails?.sectionId || null,

          // Phone Order
          customerPhone: orderData.customerPhone || orderData.sourceDetails?.customerPhone || null,
          deliveryAddress: orderData.deliveryAddress || orderData.sourceDetails?.deliveryAddress || null,
          deliveryType: orderData.deliveryType || orderData.sourceDetails?.deliveryType || null, // pickup, delivery

          // Counter Sale
          counterNumber: orderData.counterNumber || orderData.sourceDetails?.counterNumber || null,
          waitingNumber: orderData.waitingNumber || orderData.sourceDetails?.waitingNumber || null,
          isScheduled: orderData.sourceDetails?.isScheduled || null,
          scheduledDate: orderData.sourceDetails?.scheduledDate || null,
          scheduledTime: orderData.sourceDetails?.scheduledTime || null,

          // QR Menu
          qrTableCode: orderData.qrTableCode || orderData.sourceDetails?.qrTableCode || null,

          // Kiosk
          kioskId: orderData.kioskId || orderData.sourceDetails?.kioskId || null,
          ticketNumber: orderData.ticketNumber || orderData.sourceDetails?.ticketNumber || null,

          // Spread any additional sourceDetails data
          ...orderData.sourceDetails
        },

        // Müşteri bilgileri
        customer: orderData.customer || {
          id: orderData.customerId || null,
          name: orderData.customerName || 'Misafir',
          phone: orderData.customerPhone || null,
          email: orderData.customerEmail || null,
          address: orderData.deliveryAddress || null,
          isVip: orderData.isVip || false
        },

        // Sipariş içeriği
        items: orderData.items || [],

        // Mali bilgiler
        subtotal: orderData.subtotal || 0,
        taxAmount: orderData.taxAmount || 0,
        discountAmount: orderData.discountAmount || 0,
        serviceCharge: orderData.serviceCharge || 0,
        deliveryFee: orderData.deliveryFee || 0,
        totalAmount: orderData.totalAmount || 0,
        paidAmount: orderData.paidAmount || 0,

        // Zaman damgaları
        timestamps: {
          ordered: new Date(),
          confirmed: orderData.confirmedAt || null,
          kitchenReceived: orderData.kitchenReceivedAt || null,
          prepared: orderData.preparedAt || null,
          served: orderData.servedAt || null,
          completed: orderData.completedAt || null,
          paid: orderData.paidAt || null
        },

        // Personel bilgileri
        staff: {
          waiter: orderData.waiterId || null,
          cashier: orderData.cashierId || null,
          chef: orderData.chefId || null,
          createdBy: orderData.createdBy || 'System'
        },

        // Mutfak bilgileri
        kitchen_status: orderData.kitchen_status || 'pending', // Explicitly save kitchen status
        kitchenNotes: orderData.kitchenNotes || '',
        specialInstructions: orderData.specialInstructions || '',
        preparationTime: orderData.preparationTime || null,
        priority: orderData.priority || 'normal', // low, normal, high, urgent

        // Meta bilgiler
        businessId: this.businessId,
        branchId: orderData.branchId || orderData.locationId || localStorage.getItem('activeBranchId') || null,
        branchName: orderData.branchName || localStorage.getItem('activeBranchName') || null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(this.ordersRef, OrdersService.cleanUndefinedValues(orderDoc));
      console.log('✅ Order created:', docRef.id, 'branchId:', orderDoc.branchId);

       // 🔔 FCM Push Bildirim Tetikle (fire-and-forget)
       const source = (orderDoc.orderSource || '').toLowerCase().trim();
       const pushSources = ['qr_menu'];
       
       if (pushSources.includes(source)) {
        const fcmEndpoint = process.env.REACT_APP_API_URL
          ? `${process.env.REACT_APP_API_URL}/api/fcm/send-order-notification`
          : 'https://us-central1-prmanager-f881b.cloudfunctions.net/webApi/api/fcm/send-order-notification';

        fetch(fcmEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            businessId: this.businessId,
            orderId: docRef.id,
            orderData: {
              orderType: orderDoc.orderSource,
              orderSource: orderDoc.orderSource,
              brandId: orderDoc.brandId || null,
              customerName: orderDoc.customer?.name || 'Müşteri',
              total: orderDoc.totalAmount || 0,
              tableNumber: orderDoc.sourceDetails?.qrTableCode || orderDoc.sourceDetails?.tableName || null
            }
          })
        }).then(r => console.log('📬 FCM bildirim gönderildi:', r.status))
          .catch(err => console.warn('⚠️ FCM bildirim hatası (non-critical):', err.message));
      }


      // 🚀 Faz 13: Eğer sipariş 'completed' veya 'delivered' olarak oluşturulmuşsa stok düşümü tetikle (Quick Sales vb.)
      if (orderDoc.status === 'completed' || orderDoc.status === 'delivered') {
        try {
          console.log('🚀 StockSync hook triggered via createOrder');
          import('./pos/StockSyncService').then(async ({ StockSyncService }) => {
            const result = await StockSyncService.deductStockFromOrder(this.businessId, { id: docRef.id, ...orderDoc });
            if (result.success && result.stockDeductionDetails) {
              await updateDoc(docRef, {
                stockDeductionDetails: result.stockDeductionDetails,
                stockDeducted: true
              });
            }
          });
        } catch (hookError) {
          console.error('⚠️ StockSync hook error in createOrder:', hookError);
        }
      }

      return docRef.id;

    } catch (error) {
      console.error('❌ Error creating order:', error);

      // ⚡ FAZ 2: Çevrimdışı (Offline) Sipariş Kuyruğu Fallback'i
      const isNetworkError = (typeof navigator !== 'undefined' && !navigator.onLine) ||
        error?.code === 'unavailable' ||
        error?.message?.includes('offline') ||
        error?.message?.includes('network') ||
        error?.message?.includes('Failed to fetch');

      if (isNetworkError && localAgentService) {
        try {
          console.warn('⚠️ [OrdersService] Ağ/Bulut bağlantısı yok! Sipariş yerel kuyruğa alınıyor...');
          const queueResult = await localAgentService.queueOrder(orderData, this.businessId);
          if (queueResult.success) {
            return queueResult.queueId;
          }
        } catch (queueErr) {
          console.error('Failed to queue offline order:', queueErr);
        }
      }

      throw error;
    }
  }

  /**
   * ⚡ FAZ 2: Çevrimdışı bekleyen siparişleri Firestore'a aktar
   */
  async syncOfflineOrders() {
    if (!localAgentService) return { success: false, error: 'LocalAgent unavailable' };
    return localAgentService.flushOfflineOrders(this.businessId, async (queuedOrder) => {
      const { _queueId, _queuedAt, _queuedTime, _syncStatus, ...cleanData } = queuedOrder;
      await this.createOrder(cleanData);
    });
  }

  /**
   * Sipariş güncelle (genel)
   * @param {string} orderId - Sipariş ID'si
   * @param {Object} updateData - Güncellenecek veriler
   */
  async updateOrder(orderId, updateData) {
    try {
      const orderDocRef = doc(this.ordersRef, orderId);
      const finalUpdateData = {
        ...updateData,
        updatedAt: new Date()
      };

      await updateDoc(orderDocRef, OrdersService.cleanUndefinedValues(finalUpdateData));
      console.log('✅ Order updated:', orderId, finalUpdateData);

      // 🚀 Faz 13: Eğer durum 'completed' veya 'delivered' olarak güncellenmişse stok düşümü tetikle
      if (updateData.status === 'completed' || updateData.status === 'delivered') {
        try {
          console.log('🚀 StockSync hook triggered via updateOrder');
          import('./pos/StockSyncService').then(async ({ StockSyncService }) => {
            const orderData = await this.getOrder(orderId);
            if (orderData && !orderData.stockDeducted) {
              const result = await StockSyncService.deductStockFromOrder(this.businessId, orderData);
              if (result.success && result.stockDeductionDetails) {
                await updateDoc(doc(this.ordersRef, orderId), OrdersService.cleanUndefinedValues({
                  stockDeductionDetails: result.stockDeductionDetails,
                  stockDeducted: true
                }));
              }
            }
          });
        } catch (hookError) {
          console.error('⚠️ StockSync hook error in updateOrder:', hookError);
        }
      }

    } catch (error) {
      console.error('❌ Error updating order:', error);
      throw error;
    }
  }

  /**
   * Sipariş durumunu güncelle
   * @param {string} orderId - Sipariş ID'si
   * @param {string} newStatus - Yeni durum
   * @param {Object} additionalData - Ek güncellenecek veriler
   */
  async updateOrderStatus(orderId, newStatus, additionalData = {}) {
    try {
      console.log('🔄 Updating order status:', { orderId, newStatus, additionalData });

      const updateData = {
        status: newStatus,
        updatedAt: new Date(),
        ...additionalData
      };

      // Durum değişikliğine göre timestamp güncelle
      switch (newStatus) {
        case 'confirmed':
          updateData['timestamps.confirmed'] = new Date();
          break;
        case 'preparing':
          updateData['timestamps.kitchenReceived'] = new Date();
          break;
        case 'ready':
          updateData['timestamps.prepared'] = new Date();
          break;
        case 'served':
          updateData['timestamps.served'] = new Date();
          break;
        case 'completed':
        case 'delivered':
          if (newStatus === 'completed') updateData['timestamps.completed'] = new Date();
          if (newStatus === 'delivered') updateData['timestamps.served'] = new Date(); // Delivered implies served/completed

          // 🚀 Faz 13: Otomatik Stok Düşümü Tetikle
          try {
            console.log(`🚀 StockSync hook triggered for ${newStatus} order`);
            import('./pos/StockSyncService').then(async ({ StockSyncService }) => {
              const orderData = await this.getOrder(orderId);
              if (orderData && !orderData.stockDeducted) {
                const result = await StockSyncService.deductStockFromOrder(this.businessId, orderData);
                if (result.success && result.stockDeductionDetails) {
                  await updateDoc(doc(this.ordersRef, orderId), {
                    stockDeductionDetails: result.stockDeductionDetails,
                    stockDeducted: true
                  });
                }
              }
            });
          } catch (hookError) {
            console.error('⚠️ StockSync hook error:', hookError);
          }
          break;
        default:
          console.warn('⚠️ Unknown order status:', newStatus);
          break;
      }

      console.log('💾 Final update data:', updateData);

      const orderDocRef = doc(this.ordersRef, orderId);
      console.log('📄 Order document reference:', orderDocRef.path);

      // First check if document exists
      try {
        const docSnapshot = await getDoc(orderDocRef);
        if (!docSnapshot.exists()) {
          throw new Error(`Order document ${orderId} does not exist in Firebase`);
        }
        console.log('✅ Order document exists, proceeding with update');
      } catch (checkError) {
        console.error('❌ Error checking document existence:', checkError);
        throw new Error(`Failed to verify order document: ${checkError.message}`);
      }

      await updateDoc(orderDocRef, OrdersService.cleanUndefinedValues(updateData));
      console.log('✅ Order status updated successfully:', orderId, newStatus);
    } catch (error) {
      console.error('❌ Error updating order status:', {
        error: error.message,
        stack: error.stack,
        orderId,
        newStatus,
        additionalData
      });
      throw error;
    }
  }

  /**
   * Ödeme durumunu güncelle
   * @param {string} orderId - Sipariş ID'si
   * @param {string} paymentStatus - Ödeme durumu
   * @param {number} paidAmount - Ödenen tutar
   * @param {Object} paymentDetails - Ödeme detayları
   */
  async updatePaymentStatus(orderId, paymentStatus, paidAmount, paymentDetails = {}) {
    try {
      const updateData = {
        paymentStatus,
        paidAmount,
        'timestamps.paid': paymentStatus === 'paid' ? new Date() : null,
        paymentDetails,
        updatedAt: new Date()
      };

      await updateDoc(doc(this.ordersRef, orderId), OrdersService.cleanUndefinedValues(updateData));
      console.log('✅ Payment status updated:', orderId, paymentStatus);
    } catch (error) {
      console.error('❌ Error updating payment status:', error);
      throw error;
    }
  }

  /**
   * Sipariş türüne göre siparişleri getir
   * @param {string} orderSource - Sipariş kaynağı
   * @param {string} status - Durum filtresi (opsiyonel)
   * @returns {Promise<Array>} Sipariş listesi
   */
  async getOrdersBySource(orderSource, status = null) {
    try {
      let q = query(
        this.ordersRef,
        where('orderSource', '==', orderSource),
        orderBy('timestamps.ordered', 'desc')
      );

      if (status) {
        q = query(
          this.ordersRef,
          where('orderSource', '==', orderSource),
          where('status', '==', status),
          orderBy('timestamps.ordered', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting orders by source:', error);
      throw error;
    }
  }

  /**
   * Aktif siparişleri getir (mutfak ekranı için)
   * @returns {Promise<Array>} Aktif sipariş listesi
   */
  async getActiveOrders() {
    try {
      const q = query(
        this.ordersRef,
        where('status', 'in', ['pending', 'confirmed', 'preparing', 'ready']),
        orderBy('priority', 'desc'),
        orderBy('timestamps.ordered', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting active orders:', error);
      throw error;
    }
  }

  /**
   * Masa siparişlerini getir
   * @param {string} tableId - Masa ID'si
   * @returns {Promise<Array>} Masa siparişleri
   */
  async getTableOrders(tableId) {
    try {
      const q = query(
        this.ordersRef,
        where('sourceDetails.tableId', '==', tableId),
        where('status', '!=', 'completed'),
        orderBy('status'),
        orderBy('timestamps.ordered', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting table orders:', error);
      throw error;
    }
  }

  /**
   * Tüm siparişleri getir
   * @param {Object} options - Filtreleme seçenekleri
   * @returns {Promise<Array>} Sipariş listesi
   */
  async getOrders(options = {}) {
    try {
      const {
        orderSource = null,
        status = null,
        branchId = null,
        limit: limitCount = null,
        orderByField = 'timestamps.ordered',
        orderDirection = 'desc'
      } = options;

      const activeBranchId = branchId || localStorage.getItem('activeBranchId');

      let q = query(this.ordersRef);

      // Branch Filtresi
      if (activeBranchId && activeBranchId !== 'all' && activeBranchId !== 'main') {
        q = query(q, where('branchId', '==', activeBranchId));
      }

      // Filtreleme
      if (orderSource) {
        q = query(q, where('orderSource', '==', orderSource));
      }

      if (status) {
        q = query(q, where('status', '==', status));
      }

      // Sıralama
      q = query(q, orderBy(orderByField, orderDirection));

      // Limit
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting orders:', error);
      throw error;
    }
  }

  /**
   * Tüm siparişleri getir (alias for getOrders without filters)
   * @returns {Promise<Array>} Tüm siparişler
   */
  async getAllOrders() {
    try {
      const snapshot = await getDocs(
        query(this.ordersRef, orderBy('timestamps.ordered', 'desc'))
      );
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('❌ Error getting all orders:', error);
      throw error;
    }
  }

  /**
   * Tekil sipariş getir
   * @param {string} orderId - Sipariş ID'si
   * @returns {Promise<Object|null>} Sipariş verisi
   */
  async getOrder(orderId) {
    try {
      const docRef = doc(this.ordersRef, orderId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('❌ Error getting order:', error);
      throw error;
    }
  }

  /**
   * Real-time sipariş dinleyicisi
   * @param {Function} callback - Değişiklik callback'i
   * @param {Object|string} options - Filtreleme seçenekleri veya orderSource string
   * @returns {Function} Unsubscribe fonksiyonu
   */
  onOrdersChange(callback, options = {}) {
    try {
      // Backward compatibility for orderSource string
      const filter = typeof options === 'string' ? { orderSource: options } : options;
      const { orderSource, startDate, endDate, branchId, limit: limitCount = 100 } = filter;

      // Active branch ID check for branch isolation
      const activeBranchId = branchId || localStorage.getItem('activeBranchId');

      let q = query(this.ordersRef);

      // Filtering by Branch (if active branch is set)
      if (activeBranchId && activeBranchId !== 'all' && activeBranchId !== 'main') {
        q = query(q, where('branchId', '==', activeBranchId));
      }

      // Filtering by Source
      if (orderSource) {
        q = query(q, where('orderSource', '==', orderSource));
      }

      // Filtering by Date Range
      if (startDate) {
        const start = startDate instanceof Date ? startDate : new Date(startDate);
        q = query(q, where('createdAt', '>=', start));
      }

      if (endDate) {
        const end = endDate instanceof Date ? endDate : new Date(endDate);
        q = query(q, where('createdAt', '<=', end));
      }

      // Ordering and Limiting
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));

      return onSnapshot(q, (snapshot) => {
        const orders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(orders);
      });
    } catch (error) {
      console.error('❌ Error setting up orders listener:', error);
      throw error;
    }
  }

  /**
   * Sipariş ID oluştur
   * @param {string} orderSource - Sipariş kaynağı
   * @returns {string} Sipariş ID
   */
  generateOrderId(orderSource) {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const timestamp = Date.now().toString().slice(-6);

    const prefixes = {
      'table_service': 'TB',
      'phone_order': 'PH',
      'counter_sale': 'TC',
      'qr_menu': 'QR',
      'kiosk': 'KK'
    };

    const prefix = prefixes[orderSource] || 'OR';
    return `${prefix}-${today}-${timestamp}`;
  }

  /**
   * Günlük sipariş numarası oluştur
   * @returns {number} Sipariş numarası
   */
  generateOrderNumber() {
    // Bu localStorage'dan alınan günlük sayacı kullanabilir
    const today = new Date().toISOString().split('T')[0];
    const key = `dailyOrderCounter_${today}`;
    let counter = parseInt(localStorage.getItem(key) || '1');
    localStorage.setItem(key, (counter + 1).toString());
    return counter;
  }

  /**
   * Sipariş silme
   * @param {string} orderId - Sipariş ID'si
   */
  async deleteOrder(orderId) {
    try {
      await deleteDoc(doc(this.ordersRef, orderId));
      console.log('✅ Order deleted:', orderId);
    } catch (error) {
      console.error('❌ Error deleting order:', error);
      throw error;
    }
  }

  /**
   * Multi-device safe PKT number generator using Firebase atomic transactions
   * @returns {Promise<string>} PKT-001, PKT-002, etc.
   */
  async generatePKTNumber() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const counterKey = `pkt_counter_${today}`;

      console.log('🔢 PKT Generation START:', {
        today,
        counterKey,
        businessId: this.businessId,
        timestamp: new Date().toISOString()
      });

      const result = await runTransaction(db, async (transaction) => {
        console.log('🔄 Transaction START - Reading counter document...');

        const counterDoc = await transaction.get(this.countersRef);

        let currentCount = 1;
        let previousData = null;

        if (counterDoc.exists()) {
          previousData = counterDoc.data();
          const previousCount = previousData[counterKey] || 0;
          currentCount = previousCount + 1;

          console.log('📊 Counter document exists:', {
            previousCount,
            newCount: currentCount,
            allCounters: previousData,
            counterKey
          });
        } else {
          console.log('📝 Counter document does not exist, creating new one with count 1');
        }

        // Update the counter atomically
        const updateData = {
          [counterKey]: currentCount,
          lastUpdated: new Date()
        };

        console.log('💾 Updating counter with data:', updateData);

        transaction.set(this.countersRef, updateData, { merge: true });

        console.log('✅ Transaction SUCCESS - Counter updated:', currentCount);
        return currentCount;
      });

      const pktNumber = `PKT-${result.toString().padStart(3, '0')}`;

      console.log('🎯 PKT Generation COMPLETE:', {
        finalNumber: pktNumber,
        rawCounter: result,
        paddedCounter: result.toString().padStart(3, '0'),
        counterKey,
        timestamp: new Date().toISOString()
      });

      return pktNumber;
    } catch (error) {
      console.error('❌ PKT Generation FAILED:', {
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });

      // Fallback to timestamp-based number if Firebase fails
      const now = new Date();
      const timePart = now.getTime().toString().slice(-3);
      const randomPart = Math.floor(Math.random() * 100).toString().padStart(2, '0');
      const fallbackPKT = `PKT-E${timePart}${randomPart}`; // 'E' prefix for Error/Fallback

      console.warn('⚠️ Using FALLBACK PKT number:', {
        fallbackPKT,
        timePart,
        randomPart
      });

      return fallbackPKT;
    }
  }

  /**
   * Multi-device safe daily short order number generator (starts at 101 each day)
   * ÖKC cihazları ve mutfak fişleri için kısa ve kolay tuşlanabilir numara üretir
   * @returns {Promise<number>} 101, 102, 103, ...
   */
  async generateDailyShortOrderNumber() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const counterKey = `daily_order_seq_${today}`;

      const result = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(this.countersRef);
        let currentCount = 101; // Günün ilk siparişi 101'den başlar

        if (counterDoc.exists()) {
          const previousData = counterDoc.data();
          const previousCount = previousData[counterKey];
          if (typeof previousCount === 'number' && previousCount >= 101) {
            currentCount = previousCount + 1;
          }
        }

        transaction.set(this.countersRef, {
          [counterKey]: currentCount,
          lastUpdated: new Date()
        }, { merge: true });

        return currentCount;
      });

      return result;
    } catch (error) {
      console.error('❌ Daily short order number generation failed, using fallback:', error);
      const key = `fallback_daily_${new Date().toISOString().split('T')[0]}`;
      const count = parseInt(localStorage.getItem(key) || '100', 10) + 1;
      localStorage.setItem(key, count.toString());
      return count;
    }
  }

  /**
   * Boş siparişi iptal et (ürün eklenmeden çıkılan siparişler)
   * @param {string} orderId - Sipariş ID'si
   */
  async cancelEmptyOrder(orderId) {
    try {
      const orderDocRef = doc(this.ordersRef, orderId);
      await updateDoc(orderDocRef, OrdersService.cleanUndefinedValues({
        status: 'cancelled',
        cancelReason: 'Ürün eklenmeden iptal edildi',
        updatedAt: new Date(),
        'timestamps.cancelled': new Date()
      }));
      console.log('✅ Empty order cancelled:', orderId);
    } catch (error) {
      console.error('❌ Error cancelling empty order:', error);
      throw error;
    }
  }

  /**
   * Gün sonu: Tüm aktif siparişleri iptal et
   * @param {string} dateString - YYYY-MM-DD formatında tarih (opsiyonel, default bugün)
   * @returns {Promise<{cancelled: number, errors: number}>}
   */
  async closeAllActiveOrders(dateString) {
    const today = dateString || new Date().toISOString().split('T')[0];
    const activeStatuses = ['pending', 'new', 'confirmed', 'in_kitchen', 'preparing', 'ready', 'delivered_to_customer'];

    let cancelled = 0;
    let errors = 0;

    try {
      const q = query(
        this.ordersRef,
        where('status', 'in', activeStatuses)
      );
      const snapshot = await getDocs(q);
      console.log(`🔚 Gün sonu: ${snapshot.docs.length} aktif sipariş kapatılacak`);

      const updatePromises = snapshot.docs.map(async (orderDoc) => {
        try {
          await updateDoc(orderDoc.ref, OrdersService.cleanUndefinedValues({
            status: 'cancelled',
            cancelReason: 'Gün sonu kapanışı',
            dayEndClosed: true,
            dayEndDate: today,
            updatedAt: new Date(),
            'timestamps.cancelled': new Date()
          }));
          cancelled++;
        } catch (err) {
          console.error('❌ Sipariş kapatılamadı:', orderDoc.id, err);
          errors++;
        }
      });

      await Promise.all(updatePromises);
      console.log(`✅ Gün sonu tamamlandı: ${cancelled} iptal, ${errors} hata`);
      return { cancelled, errors };
    } catch (error) {
      console.error('❌ closeAllActiveOrders hatası:', error);
      throw error;
    }
  }
}

/**
 * Business ID'ye göre orders service instance oluştur
 * @param {string} businessId - İşletme ID'si
 * @returns {OrdersService} Orders service instance
 */
export const createOrdersService = (businessId) => {
  if (!businessId) {
    throw new Error('Business ID is required');
  }
  return new OrdersService(businessId);
};

// Default export
export default OrdersService;